import {nextTick, onBeforeUnmount, ref, watch, type Ref} from 'vue';
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  type Placement,
} from '@floating-ui/dom';

export interface UseDropdownOptions {
  /** Preferred placement of the menu relative to the trigger. */
  placement?: Placement;
  /** Gap in pixels between trigger and menu. Default 4. */
  offset?: number;
}

export interface UseDropdownReturn {
  /** Reactive open/closed state. Bind to template via `:class="{ show: isOpen }"`. */
  isOpen: Ref<boolean>;
  /** Template ref for the dropdown root element. */
  rootRef: Ref<HTMLElement | null>;
  toggle: () => void;
  open: () => void;
  close: () => void;
}

/** Vue-managed dropdown state. Replaces Bootstrap's `data-bs-toggle="dropdown"`. */
export function useDropdown(options: UseDropdownOptions = {}): UseDropdownReturn {
  const placement     = options.placement ?? 'bottom-start';
  const offsetPx      = options.offset    ?? 4;

  const isOpen        = ref(false);
  const rootRef       = ref<HTMLElement | null>(null);

  let positionCleanup: (() => void) | null = null;

  function onDocPointerDown(e: MouseEvent): void {
    if (!isOpen.value || !rootRef.value) return;
    if (rootRef.value.contains(e.target as Node)) return;
    close();
  }

  function onEsc(e: KeyboardEvent): void {
    if (e.key === 'Escape' && isOpen.value) close();
  }

  function onOtherDropdownOpen(): void {
    if (isOpen.value) close();
  }

  function open(): void {
    if (isOpen.value) return;
    dropdownBus.dispatchEvent(new Event('dropdown:open'));
    isOpen.value = true;
    document.addEventListener('mousedown', onDocPointerDown);
    document.addEventListener('keydown', onEsc);
  }

  function close(): void {
    if (!isOpen.value) return;
    isOpen.value = false;
    document.removeEventListener('mousedown', onDocPointerDown);
    document.removeEventListener('keydown', onEsc);
    stopPositioning();
  }

  function toggle(): void {
    isOpen.value ? close() : open();
  }

  /** Start positioning the .dropdown-menu against the wrapper element using… */
  function startPositioning(): void {
    const anchor = rootRef.value;
    if (!anchor) return;
    const menu = anchor.querySelector<HTMLElement>('.dropdown-menu');
    if (!menu) return;

    positionCleanup = autoUpdate(anchor, menu, () => {
      void computePosition(anchor, menu, {
        strategy:   'absolute',
        placement,
        middleware: [offset(offsetPx), flip(), shift({padding: 8})],
      }).then(({x, y}) => {
        Object.assign(menu.style, {
          position: 'absolute',
          top:      `${y}px`,
          left:     `${x}px`,
          margin:   '0',
        });
      });
    });
  }

  function stopPositioning(): void {
    positionCleanup?.();
    positionCleanup = null;
    const menu = rootRef.value?.querySelector<HTMLElement>('.dropdown-menu');
    if (menu) {
      menu.style.position = '';
      menu.style.top      = '';
      menu.style.left     = '';
      menu.style.margin   = '';
    }
  }

  // Activate positioning when the menu actually mounts (after Vue's DOM update).
  watch(isOpen, (open) => {
    if (open) {
      void nextTick().then(startPositioning);
    } else {
      stopPositioning();
    }
  });

  dropdownBus.addEventListener('dropdown:open', onOtherDropdownOpen);

  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onDocPointerDown);
    document.removeEventListener('keydown', onEsc);
    dropdownBus.removeEventListener('dropdown:open', onOtherDropdownOpen);
    stopPositioning();
  });

  return {isOpen, rootRef, toggle, open, close};
}

const dropdownBus = new EventTarget();
